/**
 * Polls and aggregates RTP-level video statistics (bitrate, dropped frames) per slot.
 * Deliberately does not hold consumers itself — it only ever asks the core pipeline for
 * "the consumer currently on this slot" (`getConsumer`) and "how should this slot be
 * described right now" (`getStateKey`), so this file has no way to accidentally get out
 * of sync with which consumer is actually attached where; that stays the core's job.
 */

import type { types as Mediasoup } from "mediasoup-client";

import { isInboundRtpVideoStats, type RtcStatsEntry } from "../../connection-stats";
import type { InboundRtpVideoStats } from "../../connection-stats";
import { videoSampleBetween } from "./video-latency";
import type { VideoComparisonSample, VideoLatencySnapshot } from "./video-latency-control";

export interface SlotMetrics extends Partial<VideoLatencySnapshot> {
  bitrateKbps: number;
  dropped: number;
  stateKey: string;
  jitterBufferMs?: number | null;
  decodeMs?: number | null;
  browserStatsAt?: number;
  decodedFps?: number | null;
  producerId?: string;
}

export interface CreateRtpStatsTrackerDeps {
  slotCount: () => number;
  getConsumer: (slot: number) => Mediasoup.Consumer | null;
  getStateKey: (slot: number) => string;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  getLatencySnapshot?: (slot: number) => VideoLatencySnapshot | undefined;
  onSample?: (slot: number, sample: VideoComparisonSample) => void;
}

interface SlotStats {
  bitrateKbps: number;
  dropped: number;
  prevBytesReceived: { bytes: number; ts: number } | null;
  previousVideo: InboundRtpVideoStats | null;
  jitterBufferMs: number | null;
  decodeMs: number | null;
  browserStatsAt: number;
  decodedFps: number | null;
}

function emptyStats(): SlotStats {
  return {
    bitrateKbps: 0,
    dropped: 0,
    prevBytesReceived: null,
    previousVideo: null,
    jitterBufferMs: null,
    decodeMs: null,
    browserStatsAt: 0,
    decodedFps: null,
  };
}

export function createRtpStatsTracker({
  slotCount,
  getConsumer,
  getStateKey,
  appendDiag,
  getLatencySnapshot,
  onSample,
}: CreateRtpStatsTrackerDeps) {
  const bySlot = new Map<number, SlotStats>();
  const metricsListeners = new Set<(slot: number, data: SlotMetrics) => void>();
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  function slotStats(slot: number): SlotStats {
    let s = bySlot.get(slot);
    if (!s) {
      s = emptyStats();
      bySlot.set(slot, s);
    }
    return s;
  }

  /** Reset one slot back to "nothing sampled yet" — a fresh or just-replaced consumer. */
  function resetSlot(slot: number) {
    bySlot.set(slot, emptyStats());
  }

  function onSlotMetrics(fn: (slot: number, data: SlotMetrics) => void) {
    metricsListeners.add(fn);
    return () => metricsListeners.delete(fn);
  }

  function emitSlotMetrics(slot: number) {
    const s = slotStats(slot);
    const data: SlotMetrics = {
      bitrateKbps: s.bitrateKbps,
      dropped: s.dropped,
      stateKey: getStateKey(slot),
      jitterBufferMs: s.jitterBufferMs,
      decodeMs: s.decodeMs,
      browserStatsAt: s.browserStatsAt,
      decodedFps: s.decodedFps,
      producerId: getConsumer(slot)?.producerId,
      ...getLatencySnapshot?.(slot),
    };
    for (const fn of metricsListeners) {
      try {
        fn(slot, data);
      } catch {
        /* listener errors are isolated */
      }
    }
  }

  function publishRtpStats() {
    for (let slot = 0; slot < slotCount(); slot += 1) {
      const c = getConsumer(slot);
      if (c && !c.closed) emitSlotMetrics(slot);
    }
  }

  async function pollSlotRtpStats(slot: number) {
    const consumer = getConsumer(slot);
    if (!consumer || consumer.closed) return;
    const s = slotStats(slot);
    try {
      const stats = await consumer.getStats();
      // A replacement consumer must not publish the old consumer's pending stats.
      if (getConsumer(slot) !== consumer || consumer.closed || bySlot.get(slot) !== s) return;
      const now = Date.now();
      s.jitterBufferMs = null;
      s.decodeMs = null;
      s.decodedFps = null;
      s.browserStatsAt = now;
      stats.forEach((entry: RtcStatsEntry) => {
        if (!isInboundRtpVideoStats(entry)) return;
        const current = { ...entry, timestamp: entry.timestamp ?? performance.now() };
        const sample = videoSampleBetween(s.previousVideo, current);
        s.jitterBufferMs = sample.jitterBufferMs;
        s.decodeMs = sample.decodeMs;
        s.decodedFps = sample.decodedFps;
        s.previousVideo = current;
        onSample?.(slot, sample);
        s.dropped = entry.framesDropped || 0;
        const bytes = entry.bytesReceived ?? 0;
        const prev = s.prevBytesReceived;
        if (prev && now > prev.ts) {
          const dtSec = (now - prev.ts) / 1000;
          const dBytes = bytes - prev.bytes;
          if (dBytes >= 0 && dtSec > 0) {
            s.bitrateKbps = Math.round((dBytes * 8) / 1000 / dtSec);
          }
        }
        s.prevBytesReceived = { bytes, ts: now };
      });
      publishRtpStats();
    } catch (err) {
      const msg = String(err);
      if (consumer.closed || /closed/i.test(msg)) return;
      appendDiag(`getStats slot=${slot + 1}: ${msg}`, { level: "debug" });
    }
  }

  async function pollAllSlotRtpStats() {
    await Promise.all(Array.from({ length: slotCount() }, (_, slot) => pollSlotRtpStats(slot)));
  }

  async function getAggregateConsumerStats(): Promise<Map<string, RtcStatsEntry>> {
    const merged = new Map<string, RtcStatsEntry>();
    for (let slot = 0; slot < slotCount(); slot += 1) {
      const consumer = getConsumer(slot);
      if (!consumer || consumer.closed) continue;
      try {
        const stats = await consumer.getStats();
        stats.forEach((entry: RtcStatsEntry, id: string) => merged.set(id, entry));
      } catch {
        /* ignore */
      }
    }
    return merged;
  }

  function startStatsPolling() {
    // Starts the periodic RTP-stats poll (bitrate/drop). Stream emission is driven
    // lazily by attachVideoConsumer/restoreSlot — the host subscribes via onSlotStream.
    if (pollInterval) return;
    pollInterval = setInterval(() => {
      void pollAllSlotRtpStats();
    }, 1000);
  }

  function stopStatsPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = null;
  }

  return {
    onSlotMetrics,
    emitSlotMetrics,
    publishRtpStats,
    pollSlotRtpStats,
    resetSlot,
    getAggregateConsumerStats,
    startStatsPolling,
    stopStatsPolling,
  };
}

export type RtpStatsTracker = ReturnType<typeof createRtpStatsTracker>;
